// @/src/core/store/ventas.store.ts

import type { Venta, Cliente } from '@/core/types';
import * as VentaService from '@/core/services/ventas.service';
import { getErrorMessage } from '@shared/utils/errores';

export interface VentasState {
    ventas: Venta[];
    allVentas: Venta[]; // Valid sales for inventory
    clientes: Cliente[];
    isLoading: boolean;
    error: string | null;
    isInitialized: boolean;
    // Estado de paginación
    currentPage: number;
    itemsPerPage: number;
    totalItems: number;
    totalPages: number;
}

function createVentasStore() {
    let _userId: string | null = null;
    const listeners: Set<(state: VentasState) => void> = new Set();
    // Almacenar todas las ventas en memoria para paginación eficiente (sin lecturas adicionales)
    let _todasLasVentas: Venta[] = [];

    let state: VentasState = {
        ventas: [],
        allVentas: [],
        clientes: [],
        isLoading: true,
        error: null,
        isInitialized: false,
        // Estado inicial de paginación
        currentPage: 1,
        itemsPerPage: 20,
        totalItems: 0,
        totalPages: 0,
    };

    const getState = (): VentasState => state;

    const notify = () => {
        listeners.forEach(listener => listener(state));
    };

    const subscribe = (listener: (state: VentasState) => void) => {
        listeners.add(listener);
        listener(state);
        return () => {
            listeners.delete(listener);
        };
    };

    const init = async (userId: string) => {
        if (state.isInitialized && _userId === userId && !state.error) {
            console.log('[VentasStore] ✅ Store ya inicializado para este usuario');
            return;
        }

        if (!userId) {
            state = { ...state, allVentas: [], error: "User ID is required", isLoading: false };
            notify();
            return;
        }

        console.log(`[VentasStore] 🔄 Inicializando store para usuario: ${userId}`);
        _userId = userId;
        state = { ...state, isLoading: true, error: null };
        notify();

        try {
            // Cargar todas las ventas una sola vez (1 lectura de Firestore)
            const [todasLasVentas, clientesData] = await Promise.all([
                VentaService.getSales(userId),
                VentaService.getClients(userId)
            ]);

            // Guardar todas las ventas en memoria
            _todasLasVentas = todasLasVentas;

            // Calcular paginación: mostrar solo los primeros 20 registros inicialmente
            const totalItems = todasLasVentas.length;
            const totalPages = Math.ceil(totalItems / state.itemsPerPage);
            const startIndex = 0;
            const endIndex = state.itemsPerPage;
            const ventasData = todasLasVentas.slice(startIndex, endIndex);

            console.log(`[VentasStore] ✅ Datos cargados - Ventas: ${ventasData.length}/${totalItems} (página 1), Clientes: ${clientesData.length}`);

            state = {
                ...state,
                ventas: ventasData,
                allVentas: todasLasVentas, // Exponer todas para inventario/filtros
                clientes: clientesData,
                isLoading: false,
                isInitialized: true,
                error: null,
                currentPage: 1,
                totalItems,
                totalPages,
            };
            notify();

        } catch (err) {
            console.error('[VentasStore] Error al inicializar:', err);
            state = { ...state, isLoading: false, isInitialized: false, error: getErrorMessage(err) || 'Error desconocido' };
            notify();
        }
    };

    const reload = async () => {
        if (!_userId) {
            console.warn('[VentasStore] No se puede recargar sin userId');
            return;
        }

        console.log('[VentasStore] 🔄 Recargando datos desde Firestore...');
        state = { ...state, isLoading: true, error: null };
        notify();

        try {
            // Recargar todas las ventas desde Firestore (1 lectura)
            const todasLasVentas = await VentaService.getSales(_userId);
            const clientesData = await VentaService.getClients(_userId);
            
            // Actualizar el cache en memoria
            _todasLasVentas = todasLasVentas;
            
            // Calcular la página actual y los registros a mostrar
            const totalItems = todasLasVentas.length;
            const totalPages = Math.ceil(totalItems / state.itemsPerPage);
            const startIndex = (state.currentPage - 1) * state.itemsPerPage;
            const endIndex = startIndex + state.itemsPerPage;
            const ventasData = todasLasVentas.slice(startIndex, endIndex);

            console.log(`[VentasStore] ✅ Datos recargados - Ventas: ${ventasData.length}/${totalItems} (página ${state.currentPage}), Clientes: ${clientesData.length}`);

            state = {
                ...state,
                ventas: ventasData,
                allVentas: todasLasVentas,
                clientes: clientesData,
                isLoading: false,
                error: null,
                totalItems,
                totalPages,
            };
            notify();

        } catch (err) {
            console.error('[VentasStore] Error al recargar:', err);
            state = { ...state, isLoading: false, error: getErrorMessage(err) || 'Error al recargar' };
            notify();
        }
    };

    const goToPage = async (page: number) => {
        if (!_userId) {
            console.warn('[VentasStore] No se puede cambiar de página sin userId');
            return;
        }

        // Validar que la página esté en el rango válido
        const totalItems = _todasLasVentas.length;
        const totalPages = Math.ceil(totalItems / state.itemsPerPage);
        
        if (page < 1 || page > totalPages) {
            console.warn(`[VentasStore] Página ${page} fuera de rango. Total de páginas: ${totalPages}`);
            return;
        }

        // Calcular los índices para la página solicitada usando datos en memoria
        const startIndex = (page - 1) * state.itemsPerPage;
        const endIndex = startIndex + state.itemsPerPage;
        const ventasData = _todasLasVentas.slice(startIndex, endIndex);

        console.log(`[VentasStore] 📄 Cambiando a página ${page} - Mostrando ${ventasData.length} registros`);

        state = {
            ...state,
            ventas: ventasData,
            // allVentas se mantiene igual, no cambia al paginar
            allVentas: _todasLasVentas, 
            currentPage: page,
            totalItems,
            totalPages,
        };
        notify();
    };

    const destroy = () => {
        console.log('[VentasStore] 🔄 Limpiando store');
        _userId = null;
        _todasLasVentas = []; 
        state = {
            ventas: [],
            allVentas: [],
            clientes: [],
            isLoading: true,
            error: null,
            isInitialized: false,
            currentPage: 1,
            itemsPerPage: 20,
            totalItems: 0,
            totalPages: 0,
        };
        listeners.clear();
    };

    return {
        subscribe,
        init,
        reload,
        destroy,
        getState,
        goToPage,
    };
}

export const ventasStore = createVentasStore();
